import { Component, Directive, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, Renderer2, SimpleChanges, TemplateRef, ViewContainerRef } from '@angular/core';
import { ButtonDirective } from './button.directive';
import { MatButtonModule } from '@angular/material/button';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@Directive({
    selector: '[combineLines]',
    standalone: true,
    host: { class: 'combine-lines-list' },
})
export class CombineLinesListDirective
{
}

@Directive({
    selector: '[fullWidthLines]',
    standalone: true,
    host: { class: 'full-width-lines-list' },
})
export class FullWidthLinesListDirective
{
}

@Directive({
    selector: '[inlineMenu]',
    standalone: true,
    host: { class: 'inline-menu-line' },
})
export class InlineMenuDirective
{
}

@Directive({
    selector: '[statusChipLine]',
    standalone: true,
    host: { class: 'status-chip-line' },
})
export class StatusChipLineDirective
{
}

@Directive({
    selector: '[authorLine]',
    standalone: true,
    host: { class: 'author-line' },
})
export class AuthorLineDirective
{
}

@Component({
    selector: 'app-limit-list-button',
    template: `<button class="limit-list-button" type="button" mat-flat-button linkButton (click)="onButtonClick.emit()">....</button>`,
    standalone: true,
    imports: [
        ButtonDirective,
        MatButtonModule,
    ],
})
export class LimitListButtonComponent
{
    @Output() onButtonClick = new EventEmitter();
}

@UntilDestroy()
@Directive({
    selector: '[limitListItems]',
    standalone: true,
})
export class LimitListItemsDirective<T> implements OnChanges
{
    @Input('limitListItems') limit: number | undefined;
    @Input('limitListItemsData') data: T[] | null | undefined;

    private limitLines = false;

    constructor(
            private _vcr: ViewContainerRef,
            private _tpl: TemplateRef<any>,
    ){
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['data'] || changes['limit']) {
            if (this.limit !== undefined && this.limit > 1) {
                this.updateLines(true);
            }
        }
    }

    private updateLines(limitLine = false) {
        this.limitLines = limitLine;
        this._vcr.clear();

        const total = this.data?.length > 0 ? ` (${this.data.length})` : null;

        if (this.limitLines && this.data?.length > this.limit) {
            for (let i = 0; i < this.limit; i++) {
                this._vcr.createEmbeddedView(this._tpl, { $implicit: { ...this.data[i], total: total }, first: i === 0 });
            }

            const componentRef = this._vcr.createComponent(LimitListButtonComponent);
            this._vcr.createEmbeddedView(this._tpl, componentRef);

            componentRef.instance.onButtonClick.pipe(untilDestroyed(this)).subscribe({
                next: () => this.updateLines(!this.limitLines)
            });
        } else if (this.data) {
            this.data.forEach((item, i) => {
                this._vcr.createEmbeddedView(this._tpl, { $implicit: { ...item, total: total }, first: i === 0 });
            });
        }
    }
}

@Directive({
    selector: '[orLine]',
    standalone: true,
    host: { class: 'or-line' },
})
export class OrLineDirective implements OnInit
{
    constructor(
            private _el: ElementRef,
            private _renderer: Renderer2,
    ) {
    }

    ngOnInit() {
        const span = this._renderer.createElement('span');
        span.classList.add('or-text');
        span.textContent = 'OR';

        const wrapper = this._renderer.createElement('span');
        this._renderer.appendChild(wrapper, span);
        this._renderer.appendChild(this._el.nativeElement, wrapper);
    }
}

@Directive({
    selector: '[loadingList]',
    standalone: true,
})
export class LoadingListDirective implements OnChanges
{
    @Input() loadingList = false;
    @Input() loadingLines: number = 0;
    @Input() width: number | string;

    private items: HTMLElement[] = [];

    constructor(
            private _el: ElementRef,
            private _renderer: Renderer2,
    ) {
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes && changes.hasOwnProperty('loadingList')) {
            if (this.loadingList) {
                this.attachLoadingLines(this.loadingLines > 0);
            } else {
                this.detachLoadingLines();
            }
        }
    }

    private attachLoadingLines(usePlaceholders: boolean) {
        if (usePlaceholders) {
            for (const node of this._el.nativeElement.querySelectorAll('mat-list-item')) {
                this._renderer.addClass(node, 'hidden');
            }

            for (let i = 0; i < this.loadingLines; i++) {
                const placeholderItem = this._renderer.createElement('mat-list-item');
                placeholderItem.classList.add('mat-mdc-list-item');
                placeholderItem.classList.add('loading-list-item');

                const placeholderSpan = this._renderer.createElement('span');
                placeholderSpan.classList.add('loading-line');
                this.width = this.width ? (typeof this.width === 'number' ? this.width + 'px' : this.width) : '100%';
                this._renderer.setAttribute(placeholderSpan, 'style', `width: ${this.width}`);
                this._renderer.appendChild(placeholderItem, placeholderSpan);

                this.items.push(placeholderItem);
                this._renderer.appendChild(this._el.nativeElement, placeholderItem);
            }
            return;
        }

        for (const node of this._el.nativeElement.querySelectorAll('mat-list-item')) {
            this._renderer.addClass(node, 'hidden');

            const item = this._renderer.createElement('mat-list-item');
            item.classList.add('mat-mdc-list-item');
            item.classList.add('loading-list-item');

            const span = this._renderer.createElement('span');
            span.classList.add('loading-line');

            if (node.querySelector('.mat-line')) {
                this._renderer.appendChild(item, node.querySelector('.mat-line'));
            }

            this._renderer.appendChild(item, span);

            this.items.push(item);
            this._renderer.appendChild(this._el.nativeElement, item);
        }
    }

    private detachLoadingLines() {
        if (this.items.length === 0) {
            return;
        }

        for (const i in this.items) {
            if (this.items[i]) {
                const item = this.items[i];
                const node = this._el.nativeElement.querySelectorAll('mat-list-item')[i];

                this._renderer.removeClass(node, 'hidden');
                if (item.querySelector('.mat-line')) {
                    this._renderer.appendChild(node.querySelector('.mat-list-text'), item.querySelector('.mat-line'));
                }

                this._renderer.removeChild(this._el.nativeElement, item);
            }
        }

        this.items = [];
    }
}

@Directive({
    selector: '[loadingListItem]',
    standalone: true,
})
export class LoadingListItemDirective implements OnChanges
{
    @Input() loadingListItem: boolean;
    @Input() width: number | string;

    private span: HTMLElement = null;

    constructor(
            private _el: ElementRef,
            private _renderer: Renderer2,
    ) {
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes && changes.hasOwnProperty('loadingListItem')) {
            if (this.loadingListItem) {
                this._el.nativeElement.classList.add('loading-list-item-line');
                this.span = this._renderer.createElement('span');
                this.span.classList.add('loading-line');
                this.width = this.width ? (typeof this.width === 'number' ? this.width + 'px' : this.width) : '100%';
                this._renderer.setAttribute(this.span, 'style', `width: ${this.width}`);
                this._renderer.appendChild(this._el.nativeElement, this.span);
            } else {
                this._el.nativeElement.classList.remove('loading-list-item-line');
                if (this.span) {
                    this._renderer.removeChild(this._el.nativeElement, this.span);
                    this.span = null;
                }
            }
        }
    }
}
